import { useEffect, useState } from 'react'
import { apiClient } from '../services/api'
import { useAuthStore } from '../services/authStore'
import { ui } from '../styles/ui'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import ConfirmDialog from '../components/ui/ConfirmDialog'
import ErrorState from '../components/ui/ErrorState'
import Input from '../components/ui/Input'
import LoadingState from '../components/ui/LoadingState'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import UserEditDialog from '../components/users/UserEditDialog'
import { useTranslation } from '../contexts/TranslationContext'

function formatRowUser(user) {
  return {
    ...user,
    is_admin: Boolean(user.is_admin ?? user.isAdmin),
  }
}

export default function UserManagement() {
  const { t } = useTranslation()
  const currentUserId = useAuthStore((state) => state.user?.id)
  const checkAuthStatus = useAuthStore((state) => state.checkAuthStatus)
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmMessage, setConfirmMessage] = useState('')
  const [editingUser, setEditingUser] = useState(null)
  const [selectedUserIds, setSelectedUserIds] = useState([])
  const [confirmIds, setConfirmIds] = useState([])
  const [savingEdit, setSavingEdit] = useState(false)

  const fetchUsers = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.get('/users')
      setUsers((response.data.users || []).map(formatRowUser))
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('Failed to load users', 'Failed to load users'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const toggleAdmin = async (user) => {
    try {
      const updated = { ...user, is_admin: !user.is_admin }
      await apiClient.put(`/users/${user.id}`, { is_admin: updated.is_admin })
      setUsers((prev) => prev.map((item) => (item.id === user.id ? updated : item)))
    } catch (err) {
      setError(err.response?.data?.message || t('Failed to update user', 'Failed to update user'))
    }
  }

  const openEditUser = (user) => {
    setEditingUser(user)
  }

  const closeEditUser = () => {
    if (savingEdit) return
    setEditingUser(null)
  }

  const deleteUser = (user) => {
    setConfirmIds([user.id])
    setConfirmMessage(t('users.delete_user_confirm', `Delete user ${user.email}?`))
    setConfirmOpen(true)
  }

  const toggleSelectUser = (id) => {
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((selected) => selected !== id) : [...prev, id],
    )
  }

  const toggleSelectAllUsers = () => {
    if (selectedUserIds.length === users.length) {
      setSelectedUserIds([])
      return
    }

    setSelectedUserIds(users.map((user) => user.id))
  }

  const handleBulkDelete = () => {
    if (!selectedUserIds.length) return
    setConfirmIds(selectedUserIds)
    setConfirmMessage(t('users.bulk_delete_confirm', `Delete ${selectedUserIds.length} selected users?`))
    setConfirmOpen(true)
  }

  const confirmDeleteUser = async () => {
    if (!confirmIds.length) return
    try {
      await apiClient.delete('/users', { data: { ids: confirmIds } })
      setUsers((prev) => prev.filter((user) => !confirmIds.includes(user.id)))
      setSelectedUserIds([])
    } catch (err) {
      setError(err.response?.data?.message || t('Failed to delete user', 'Failed to delete user'))
    } finally {
      setConfirmOpen(false)
      setConfirmIds([])
    }
  }

  const cancelDeleteUser = () => {
    setConfirmOpen(false)
    setConfirmIds([])
  }

  const syncCurrentUserSession = async (updatedUser) => {
    if (!updatedUser?.id || updatedUser.id !== currentUserId) {
      return
    }

    localStorage.setItem('user', JSON.stringify(updatedUser))
    await checkAuthStatus()
  }

  const handleSaveUser = async (payload) => {
    if (!editingUser) return
    setSavingEdit(true)
    try {
      const response = await apiClient.put(`/users/${editingUser.id}`, payload)
      const updatedUser = formatRowUser(response.data.user || {})
      setUsers((prev) => prev.map((item) => (item.id === editingUser.id ? { ...item, ...updatedUser } : item)))
      try {
        await syncCurrentUserSession(updatedUser)
      } catch (sessionError) {
        console.warn('Failed to refresh current user session after admin edit', sessionError)
      }
      setError(null)
      setEditingUser(null)
    } finally {
      setSavingEdit(false)
    }
  }

  if (loading) {
    return (
      <PageShell>
        <LoadingState text={t('Loading users...', 'Loading users...')} />
      </PageShell>
    )
  }

  return (
    <PageShell>
      <PageHeader
        title={t('Users', 'Users')}
        actions={(
          <div className="flex items-center gap-2">
            <Input placeholder={t('Search by name or email', 'Search by name or email')} className="w-full max-w-sm" />
            <Button variant="primary" size="sm" onClick={fetchUsers}>
              {t('Refresh', 'Refresh')}
            </Button>
          </div>
        )}
      />
      {error && <ErrorState text={error} />}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-outline bg-surface-elevated p-4 text-sm text-foreground">
        <div>
          {selectedUserIds.length > 0 && (
            <span>
              {selectedUserIds.length} {t('user', 'user')}
              {selectedUserIds.length === 1 ? '' : t('s', 's')} {t('selected', 'selected')}
            </span>
          )}
        </div>
        <Button
          variant="danger"
          size="sm"
          onClick={handleBulkDelete}
          disabled={!selectedUserIds.length}
        >
          {t('Delete selected', 'Delete selected')}
        </Button>
      </div>

      <Card>
        <div className="overflow-hidden rounded-lg border border-outline">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className={ui.tableHead}>
              <tr>
                <th className={ui.tableHeadCell}>
                  <input
                    type="checkbox"
                    checked={selectedUserIds.length === users.length && users.length > 0}
                    onChange={toggleSelectAllUsers}
                  />
                </th>
                <th className={ui.tableHeadCell}>ID</th>
                <th className={ui.tableHeadCell}>{t('Name', 'Name')}</th>
                <th className={ui.tableHeadCell}>{t('Email', 'Email')}</th>
                <th className={ui.tableHeadCell}>{t('Admin', 'Admin')}</th>
                <th className={ui.tableHeadCell}>{t('Actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="bg-surface hover:bg-surface-elevated">
                  <td className={ui.tableCell}>
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(user.id)}
                      onChange={() => toggleSelectUser(user.id)}
                    />
                  </td>
                  <td className={ui.tableCellText}>{user.id}</td>
                  <td className={ui.tableCellText}>{user.name}</td>
                  <td className={ui.tableCellText}>{user.email}</td>
                  <td className={ui.tableCell}>
                    {user.is_admin ? (
                      <span className={`${ui.badge} ${ui.badgeSuccess}`}>{t('Admin', 'Admin')}</span>
                    ) : (
                      <span className={`${ui.badge} ${ui.badgeNeutral}`}>{t('User', 'User')}</span>
                    )}
                  </td>
                  <td className={`${ui.tableCell} flex flex-wrap gap-2`}>
                    <Button variant="secondary" size="sm" onClick={() => openEditUser(user)}>
                      {t('Edit', 'Edit')}
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => toggleAdmin(user)}>
                      {user.is_admin ? t('Revoke', 'Revoke') : t('Promote', 'Promote')}
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => deleteUser(user)}>
                      {t('Delete', 'Delete')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <UserEditDialog
        open={Boolean(editingUser)}
        user={editingUser}
        saving={savingEdit}
        onCancel={closeEditUser}
        onSave={handleSaveUser}
      />

      <ConfirmDialog
        open={confirmOpen}
        title={t('Delete User', 'Delete User')}
        message={confirmMessage}
        onConfirm={confirmDeleteUser}
        onCancel={cancelDeleteUser}
        confirmText={t('Delete', 'Delete')}
        cancelText={t('Cancel', 'Cancel')}
      />
    </PageShell>
  )
}
